import { Modal, Table, Input, Tooltip, Tag, message, Button } from "antd";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { http } from "../../services/http";
import { getToday } from "../../utils/getDays";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { numberWithSpacesIntl } from "../../utils/numberWithSpaces";

const ActSverkaForClients = ({ visible, datas, onClose }) => {
  const today = getToday();

  const { t } = useTranslation();
  const pdfRef = useRef(null);

  const [data] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(today);
  const [cardCode, setCardCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (datas) {
      setCardCode(datas.cardCode);
    }
  }, [datas]);

  const columnsSalesAndReturn = [
    {
      title: t("cardName"),
      dataIndex: "cardName",
      key: "cardName",
    },
    {
      title: t("docDate"),
      dataIndex: "docDate",
      key: "docDate",
      render: (text) => {
        const formattedText = moment(text).format("DD.MM.YYYY");
        return `${formattedText}`;
      },
    },
    {
      title: t("item"),
      dataIndex: "itemName",
      key: "itemName",
    },

    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText}`;
      },
    },
    {
      title: t("unitMsr"),
      dataIndex: "unitMsr",
      key: "unitMsr",
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText} $`;
      },
    },
    {
      title: t("lineTotal"),
      dataIndex: "lineTotal",
      key: "lineTotal",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText} $`;
      },
    },
    {
      title: t("invQty"),
      dataIndex: "invQty",
      key: "invQty",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText}`;
      },
    },
    {
      title: t("ugpName"),
      dataIndex: "ugpName",
      key: "ugpName",
    },
    {
      title: t("whsName"),
      dataIndex: "whsName",
      key: "whsName",
    },
  ];
  const columnsPayments = [
    {
      title: t("cardName"),
      dataIndex: "cardName",
      key: "cardName",
    },
    {
      title: t("docDate"),
      dataIndex: "docDate",
      key: "docDate",
      render: (text) => {
        const formattedText = moment(text).format("DD.MM.YYYY");
        return `${formattedText}`;
      },
    },
    {
      title: t("paymentType"),
      dataIndex: "docType",
      key: "docType",
      render: (text) => {
        if (text === "incoming") {
          return (
            <Tag className="rounded-xl" color="green">
              {t("Incoming")}
            </Tag>
          );
        }
        if (text === "outgoing") {
          return (
            <Tag className="rounded-xl" color="red">
              {t("Outgoing")}
            </Tag>
          );
        }
        return <Tag className="rounded-xl">{text}</Tag>;
      },
    },

    {
      title: t("acctName"),
      dataIndex: "acctName",
      key: "acctName",
    },

    {
      title: t("paymentInUZS"),
      dataIndex: "cashSumFC",
      key: "cashSumFC",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText} so'm`;
      },
    },
    {
      title: t("docRate"),
      dataIndex: "docRate",
      key: "docRate",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText}`;
      },
    },
    {
      title: t("paymentInUSD"),
      dataIndex: "cashSum",
      key: "cashSum",
      render: (text) => {
        const formattedText = numberWithSpacesIntl(text);
        return `${formattedText} $`;
      },
    },
    {
      title: t("comment"),
      dataIndex: "u_Firma",
      key: "u_Firma",
      render: (text, record) => {
        return (
          text && (
            <Tooltip title={text}>
              <div className="cursor-pointer">{`${text && text.length > 30 ? `${text.slice(0, 30)}...` : text}`}</div>
            </Tooltip>
          )
        );
      },
    },
  ];

  const fetchAktSverkaInvoices = async ({ queryKey }) => {
    const [_, cardCode, startDate, endDate] = queryKey;
    try {
      const { data } = await http.get(
        `api/mijozaktsverka/invoices?cardCode=${cardCode}&startDate=${startDate}&endDate=${endDate}`,
      );
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const fetchAktSverkaPayments = async ({ queryKey }) => {
    const [_, cardCode, startDate, endDate] = queryKey;
    try {
      const { data } = await http.get(
        `api/mijozaktsverka/payments?cardCode=${cardCode}&startDate=${startDate}&endDate=${endDate}`,
      );
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const fetchAktSverkaCreditNotes = async ({ queryKey }) => {
    const [_, cardCode, startDate, endDate] = queryKey;
    try {
      const { data } = await http.get(
        `api/mijozaktsverka/credit-notes?cardCode=${cardCode}&startDate=${startDate}&endDate=${endDate}`,
      );
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const fetchBalance = async ({ queryKey }) => {
    const [_, cardCode, startDate, endDate] = queryKey;
    try {
      const { data } = await http.get(
        `api/mijozaktsverka/balances?cardCode=${cardCode}&startDate=${startDate}&endDate=${endDate}`,
      );
      return data[0];
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const { data: sales = [], isLoading: isLoadingSales } = useQuery({
    queryKey: ["aktSverkaInvoices", cardCode, startDate, endDate],
    queryFn: fetchAktSverkaInvoices,
    enabled: !!cardCode,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: payments = [], isLoading: isLoadingPayments } = useQuery({
    queryKey: ["aktSverkaPayments", cardCode, startDate, endDate],
    queryFn: fetchAktSverkaPayments,
    enabled: !!cardCode,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: creditNotes = [], isLoading: isLoadingCreditNotes } = useQuery({
    queryKey: ["aktSverkaCreditNotes", cardCode, startDate, endDate],
    queryFn: fetchAktSverkaCreditNotes,
    enabled: !!cardCode,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  const { data: balance = [], isLoading: isLoadingBalance } = useQuery({
    queryKey: ["aktSverkaBalance", cardCode, startDate, endDate],
    queryFn: fetchBalance,
    enabled: !!cardCode,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const handleExportExcel = async ({ startDate, endDate, cardCode }) => {
    setLoading(true);
    try {
      const res = await http.get(
        `api/mijozaktsverka/excel?startDate=${startDate}&endDate=${endDate}&cardCode=${cardCode}`,
        {
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseType: "blob", // Correct position
        },
      );

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${datas.cardName}(${startDate}-${endDate}).xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
    setLoading(false);
  };

  const balanceFirstDayOfPeriod = numberWithSpacesIntl(
    balance?.beginningBalance,
  );
  const balanceLastDayOfPeriod = numberWithSpacesIntl(balance?.endingBalance);

  return (
    <Modal
      title={
        <h1 className="font-nunito text-xl font-extrabold text-[#000000]">
          {t("mijoz")} : {datas?.cardName}
        </h1>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="95%"
      style={{ height: "85%" }}
      bodyStyle={{ height: "calc(100% - 55px)", overflowY: "auto" }}
    >
      <div className="w-full px-5" ref={pdfRef}>
        <div className="mt-5 flex flex-col items-end justify-between sm:flex-row">
          <div className="flex flex-col gap-2">
            <p className="font-nunito">{t("mijoz")}</p>
            <Input
              type="text"
              style={{ color: "black" }}
              value={datas?.cardName}
              className="flex h-8 w-full items-center justify-center sm:w-[250px]"
              disabled
            />
          </div>
          <div className="ml-10 flex gap-5">
            <div className="flex flex-col gap-3">
              <p>{t("startDate")}</p>
              <Input
                type="date"
                value={startDate}
                className="w-[150px]"
                onChange={(e) => {
                  setStartDate(e.target.value);
                }}
                max={endDate}
              />
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("endDate")}</p>
              <Input
                type="date"
                value={endDate}
                className="w-[150px]"
                onChange={(e) => {
                  setEndDate(e.target.value);
                }}
                min={startDate}
                max={today}
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col items-end justify-end gap-5 sm:flex-row">
          <div className="flex flex-col gap-2">
            <p className="font-nunito">{t("debtInStartDate")}</p>
            <Input
              type="text"
              style={{ color: "black" }}
              value={`${balanceFirstDayOfPeriod} $`}
              className="flex h-8 w-full items-center justify-center sm:w-[250px]"
              disabled
            />
          </div>
          <div>
            <Button
              loading={loading}
              className="bg-[#0A4D68] text-white"
              onClick={() =>
                handleExportExcel({
                  startDate,
                  endDate,
                  cardCode: datas?.cardCode,
                })
              }
            >
              {t("exportToExcel")}
            </Button>
          </div>
        </div>

        <div className="mt-10 flex w-full flex-col gap-5">
          <p className="font-nunito text-center text-xl font-bold">
            {t("actSverkaSales")}
          </p>
          <Table
            columns={columnsSalesAndReturn}
            dataSource={sales}
            pagination={false}
            loading={isLoadingSales}
            sticky={true}
            className="border-1 border-black"
            summary={(pageData) => {
              const totalQuantity = pageData.reduce(
                (total, item) => total + item.quantity,
                0,
              );
              const total = pageData.reduce(
                (total, item) => total + item.lineTotal,
                0,
              );
              const totalInvQty = pageData.reduce(
                (total, item) => total + item.invQty,
                0,
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={1}
                      colSpan={1}
                      className="font-bold text-black"
                    >
                      {t("total")}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={2}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={3}
                      className="font-bold text-black"
                    >
                      {numberWithSpacesIntl(totalQuantity)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={4}
                      colSpan={2}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      className="font-bold text-black"
                    >
                      {`${numberWithSpacesIntl(total)} $`}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell
                      index={6}
                      className="font-bold text-black"
                    >
                      {`${numberWithSpacesIntl(totalInvQty)}`}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>
        <div className="mt-10 flex w-full flex-col gap-5">
          <p className="font-nunito text-center text-xl font-bold">
            {t("actSverkaCreditNotes")}
          </p>
          <Table
            columns={columnsSalesAndReturn}
            dataSource={creditNotes}
            pagination={false}
            loading={isLoadingCreditNotes}
            sticky={true}
            className="border-1 border-black"
            summary={(pageData) => {
              const totalQuantity = pageData.reduce(
                (total, item) => total + item.quantity,
                0,
              );
              const total = pageData.reduce(
                (total, item) => total + item.lineTotal,
                0,
              );
              const totalInvQty = pageData.reduce(
                (total, item) => total + item.invQty,
                0,
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={1}
                      colSpan={1}
                      className="font-bold text-black"
                    >
                      {t("total")}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={2}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={3}
                      className="font-bold text-black"
                    >
                      {numberWithSpacesIntl(totalQuantity)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={4}
                      colSpan={2}
                      className="font-bold text-black"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      className="font-bold text-black"
                    >
                      {`${numberWithSpacesIntl(total)} $`}
                    </Table.Summary.Cell>

                    <Table.Summary.Cell
                      index={6}
                      className="font-bold text-black"
                    >
                      {`${numberWithSpacesIntl(totalInvQty)}`}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>
        <div className="mt-10 flex w-full flex-col gap-5">
          <p className="font-nunito text-center text-xl font-bold">
            {t("actSverkaPayments")}
          </p>
          <Table
            columns={columnsPayments}
            dataSource={payments}
            pagination={false}
            loading={isLoadingPayments}
            sticky={true}
            className="border-1 border-black"
            summary={(pageData) => {
              let total = 0;
              pageData.forEach((item) => {
                total += item.cashSum;
              });
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      colSpan={4}
                      index={0}
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={2} index={1}>
                      <span className="font-bold">{t("total")}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} className="font-bold">
                      {numberWithSpacesIntl(total)} $
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>

        <div className="mt-5 flex flex-col justify-end sm:flex-row">
          <div className="flex flex-col gap-2">
            <p className="font-nunito">{t("debtEndDate")}</p>
            <Input
              type="text"
              style={{ color: "black" }}
              value={`${balanceLastDayOfPeriod} $`}
              className="flex h-8 w-full items-center justify-center sm:w-[250px]"
              disabled
            />
          </div>
        </div>

        <div className="mt-8 pt-6">
          <div className="flex justify-end">
            <table className="w-1/3 border border-black text-sm">
              <tbody>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("startDate")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {moment(startDate).format("DD.MM.YYYY")}
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("endDate")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {moment(endDate).format("DD.MM.YYYY")}
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("cardName")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {datas?.cardName}
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("debtInStartDate")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {balanceFirstDayOfPeriod} $
                  </td>
                </tr>

                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("salesTotal")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {numberWithSpacesIntl(
                      sales?.reduce((acc, item) => acc + item.lineTotal, 0),
                    )}{" "}
                    $
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("returnTotal")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {numberWithSpacesIntl(
                      creditNotes?.reduce(
                        (acc, item) => acc + item.lineTotal,
                        0,
                      ),
                    )}{" "}
                    $
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("paymentTotal")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {numberWithSpacesIntl(
                      payments?.reduce((acc, item) => acc + item.cashSum, 0),
                    )}{" "}
                    $
                  </td>
                </tr>
                <tr>
                  <td className="print-bg-color border border-black bg-sky-700 px-2 py-2 font-bold text-white print:bg-sky-700">
                    {t("debtEndDate")}
                  </td>
                  <td className="border border-black px-2 py-2 font-bold">
                    {balanceLastDayOfPeriod} $
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ActSverkaForClients;
